import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Inbox, Plus, Trash2, KeyRound } from "lucide-react";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-10 rounded-sm placeholder:text-zinc-600";

export const TechsTab = ({ onAuthError }) => {
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [saving, setSaving] = useState(false);

  const fetchTechs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/techs`);
      setTechs(res.data);
    } catch (err) {
      if (!onAuthError(err)) toast.error("Failed to load technicians");
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => { fetchTechs(); }, [fetchTechs]);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password (8+) are required.");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/techs`, form);
      toast.success("Technician added — they can sign in at /tech");
      setAddOpen(false);
      setForm({ name: "", email: "", phone: "", password: "" });
      fetchTechs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add technician");
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (t) => {
    const pw = window.prompt(`New password for ${t.name} (8+ characters):`);
    if (!pw) return;
    try {
      await axios.patch(`${API}/admin/techs/${t.tech_id}`, { password: pw });
      toast.success("Password updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const toggleActive = async (t) => {
    try {
      await axios.patch(`${API}/admin/techs/${t.tech_id}`, { active: !t.active });
      toast.success(t.active ? "Technician deactivated" : "Technician reactivated");
      fetchTechs();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete technician ${t.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/techs/${t.tech_id}`);
      toast.success("Technician deleted");
      fetchTechs();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="techs-tab">
      <div className="flex justify-between items-center mb-6">
        <p className="text-zinc-500 text-sm">Technicians sign in at <span className="text-blue-400">{window.location.origin}/tech</span></p>
        <button data-testid="add-tech-button" onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-4 h-10 text-sm font-semibold">
          <Plus className="h-4 w-4" /> Add Technician
        </button>
      </div>
      {techs.length === 0 ? (
        <div data-testid="techs-empty-state" className="text-center py-24 text-zinc-500"><Inbox className="h-12 w-12 mx-auto mb-4 text-zinc-700" />No technicians yet.</div>
      ) : (
        <div className="space-y-3">
          {techs.map((t) => (
            <div key={t.tech_id} data-testid={`tech-row-${t.tech_id}`} className="bg-[#121212] border border-zinc-800 rounded-xl p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-heading font-semibold text-white">{t.name}</div>
                <div className="text-zinc-500 text-xs">{t.email}{t.phone ? ` · ${t.phone}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`rounded-sm ${t.active ? "bg-green-600/20 text-green-400 border-green-500/40" : "bg-zinc-600/20 text-zinc-400 border-zinc-500/40"}`}>{t.active ? "Active" : "Inactive"}</Badge>
                <button data-testid={`tech-toggle-${t.tech_id}`} onClick={() => toggleActive(t)} className="border border-zinc-700 text-zinc-300 rounded-sm px-3 h-9 text-xs font-semibold hover:border-zinc-500">{t.active ? "Deactivate" : "Activate"}</button>
                <button data-testid={`tech-reset-${t.tech_id}`} onClick={() => resetPassword(t)} className="p-2 border border-zinc-700 rounded-sm text-zinc-400 hover:text-white" title="Reset password"><KeyRound className="h-4 w-4" /></button>
                <button data-testid={`tech-delete-${t.tech_id}`} onClick={() => remove(t)} className="p-2 border border-zinc-800 rounded-sm text-zinc-500 hover:text-red-400" title="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add Technician</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name", "Full name *"], ["email", "Email (their login) *"], ["phone", "Phone (receives job reminders)"], ["password", "Password (8+ chars) *"]].map(([k, label]) => (
              <Input key={k} data-testid={`tech-form-${k}`} type={k === "password" ? "password" : "text"} className={inputCls} placeholder={label} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
            ))}
          </div>
          <DialogFooter>
            <button data-testid="tech-form-save" onClick={create} disabled={saving} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm px-6 py-2.5 font-semibold">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Add Technician
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
